import type { KeyboardEvent, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Sparkles } from 'lucide-react';
import { toneBackgroundImage, topicById, type LearnsTopicId } from '@/constants/learns-data';
import type { ArticleView } from '@/types/api';

/**
 * LearnsFeaturedCard — the big 2-column hero card immediately under the
 * page hero. Left column is an illustration; right column is the copy
 * block ending in the "Start reading →" CTA.
 *
 * The whole card is clickable (navigates to the article route). Hover
 * only adds shadow — no lift — because a card this big shouldn't wobble.
 *
 * Illustration is an inline SVG so it ships with zero extra requests and
 * colour-matches the warm stripe palette on its 16px-radius crop. The
 * scene follows the spec §6a: sun glow → far mountains → stratovolcano
 * + shadow face → lava + crater → embers → smoke plumes → wavy ground.
 *
 * Takes an `article` prop (an ArticleView from the API). When null — the
 * server has no featured article live yet — the component renders a
 * quiet empty state so the page still flows without a hole.
 */
export interface LearnsFeaturedCardProps {
  article: ArticleView | null;
}

export function LearnsFeaturedCard({ article }: LearnsFeaturedCardProps) {
  const navigate = useNavigate();

  if (!article) return <FeaturedEmptyState />;

  const topic = topicById(article.topic as LearnsTopicId);

  const go = () => navigate(`/learns/articles/${article.slug}`);

  const handleCardClick = () => go();
  const handleCardKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      go();
    }
  };
  const handleCtaClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    go();
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKey}
      aria-label={`Featured today: ${article.title}. Start reading.`}
      className="group relative grid cursor-pointer grid-cols-1 items-stretch gap-3 transition-shadow duration-200 ease-out hover:shadow-[0_6px_20px_rgba(31,27,22,0.06),0_1px_2px_rgba(31,27,22,0.04)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E8A8A]/40 focus-visible:ring-offset-2 md:grid-cols-[1.2fr_1fr] md:gap-7"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #EFE5D2',
        borderRadius: '22px',
        padding: '10px',
      }}
    >
      {/* --- Left column: illustration ---
          If the admin has uploaded a thumbnail we render it edge-to-edge
          inside the same 16px-radius crop. Otherwise we fall back to the
          tone-stripe + volcano illustration so the card still feels
          finished even before art has landed. */}
      <div
        className="min-h-[180px] md:min-h-[320px]"
        style={{
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundImage: article.thumbnailImage ? undefined : toneBackgroundImage('warm'),
          backgroundColor: article.thumbnailImage ? '#F4ECE0' : undefined,
          position: 'relative',
        }}
        aria-hidden
      >
        {article.thumbnailImage ? (
          <img
            src={article.thumbnailImage}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <VolcanoIllustration />
        )}
      </div>

      {/* --- Right column: content --- */}
      <div className="flex flex-col justify-between px-[18px] pb-[18px] pt-2 md:py-[22px] md:pl-1 md:pr-6">
        <div>
          {/* Badge row */}
          <div className="flex items-center" style={{ gap: '8px', marginBottom: '14px' }}>
            <span
              className="inline-flex items-center"
              style={{
                gap: '4px',
                backgroundColor: '#2E8A8A',
                color: '#FFFFFF',
                fontFamily: 'Inter, sans-serif',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                padding: '4px 9px',
                borderRadius: '999px',
                textTransform: 'uppercase',
              }}
            >
              Featured Today
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: topic.accent,
              }}
            >
              {topic.label}
            </span>
          </div>

          {/* Title */}
          <h2
            className="text-[26px] md:text-[34px]"
            style={{
              fontFamily: '"DM Serif Display", serif',
              fontWeight: 400,
              lineHeight: 1.08,
              letterSpacing: '-0.01em',
              color: '#1F1B16',
              margin: '0 0 14px',
            }}
          >
            {article.title}
          </h2>

          {/* Summary */}
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: 1.55,
              color: '#3D342A',
              margin: 0,
            }}
          >
            {article.summary}
          </p>
        </div>

        <div style={{ marginTop: '20px' }}>
          {/* Meta row */}
          <div
            className="flex flex-wrap items-center"
            style={{
              gap: '14px',
              marginBottom: '16px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              color: '#8A7F72',
            }}
          >
            <MetaItem>
              <Clock style={metaIconStyle} strokeWidth={2} aria-hidden />
              {article.mins} min read
            </MetaItem>
            {article.author ? (
              <MetaItem>
                <Sparkles style={metaIconStyle} strokeWidth={2} aria-hidden />
                {article.author}
              </MetaItem>
            ) : null}
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={handleCtaClick}
            className="inline-flex items-center transition-colors"
            style={{
              backgroundColor: '#2E8A8A',
              color: '#FFFFFF',
              padding: '10px 18px',
              borderRadius: '999px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              gap: '8px',
              boxShadow: '0 1px 2px rgba(31, 27, 22, 0.04)',
              border: 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1F6A6A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2E8A8A';
            }}
            onFocus={(e) => {
              e.currentTarget.style.backgroundColor = '#1F6A6A';
            }}
            onBlur={(e) => {
              e.currentTarget.style.backgroundColor = '#2E8A8A';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(1px)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Start reading
            <ArrowRight
              style={{ width: '14px', height: '14px', color: 'currentColor' }}
              strokeWidth={2}
              aria-hidden
            />
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Helpers --------------------------------------------------------------

const metaIconStyle: React.CSSProperties = {
  width: '12px',
  height: '12px',
  color: '#8A7F72',
  flexShrink: 0,
};

function MetaItem({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center" style={{ gap: '6px' }}>
      {children}
    </span>
  );
}

/**
 * Inline volcano illustration for the featured card. Follows the spec's
 * layered recipe: radial sun glow → far-mountain silhouettes → main
 * stratovolcano body + shadow face → lava-filled crater → ember sparks →
 * smoke plumes → wavy ground. 400×320 viewBox, preserved via
 * `xMidYMid slice` so the scene fills the 16px-radius crop regardless of
 * card width.
 */
function VolcanoIllustration() {
  return (
    <svg
      viewBox="0 0 400 320"
      preserveAspectRatio="xMidYMid slice"
      width="100%"
      height="100%"
      style={{ display: 'block' }}
      aria-hidden
    >
      <defs>
        <radialGradient id="learns-sun-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#FFD9A6" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#FFD9A6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FFD9A6" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="learns-lava" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD96B" />
          <stop offset="55%" stopColor="#F28331" />
          <stop offset="100%" stopColor="#C4351A" />
        </linearGradient>
      </defs>

      {/* Sun glow (top-right) */}
      <circle cx="350" cy="60" r="100" fill="url(#learns-sun-glow)" />

      {/* Far mountain silhouettes */}
      <polygon points="30,270 130,180 230,270" fill="#8E6E4A" opacity="0.4" />
      <polygon points="200,270 300,195 390,270" fill="#8E6E4A" opacity="0.5" />

      {/* Main stratovolcano body */}
      <polygon points="70,275 205,95 335,275" fill="#4A311A" />
      {/* Shadow face (right side) */}
      <polygon points="205,95 335,275 250,190" fill="#2D1C0E" />

      {/* Crater cap — trapezoid at the summit */}
      <polygon points="186,100 224,100 232,118 178,118" fill="#2D1C0E" />

      {/* Lava pool on the crater rim + flowing down one side */}
      <path
        d="M 186 108
           Q 205 98 224 108
           L 230 124
           Q 236 148 226 168
           Q 235 192 218 214
           Q 224 232 214 252
           Q 222 268 210 278
           L 198 278
           Q 190 266 196 250
           Q 185 230 194 210
           Q 182 190 192 168
           Q 180 146 186 122 Z"
        fill="url(#learns-lava)"
      />

      {/* Ember sparks above the crater */}
      <circle cx="196" cy="80" r="2.5" fill="#FFD26B" />
      <circle cx="214" cy="70" r="2" fill="#FFD26B" />
      <circle cx="205" cy="58" r="1.5" fill="#FFD26B" />
      <circle cx="222" cy="86" r="1.8" fill="#FFD26B" />
      <circle cx="188" cy="64" r="1.5" fill="#FFD26B" />
      <circle cx="230" cy="54" r="1.4" fill="#FFD26B" />
      <circle cx="200" cy="44" r="1.2" fill="#FFD26B" />

      {/* Smoke plumes — stacked ellipses rising from the crater */}
      <ellipse cx="210" cy="78" rx="26" ry="10" fill="#5B4530" opacity="0.6" />
      <ellipse cx="222" cy="58" rx="32" ry="12" fill="#5B4530" opacity="0.5" />
      <ellipse cx="208" cy="36" rx="38" ry="14" fill="#5B4530" opacity="0.42" />
      <ellipse cx="226" cy="16" rx="44" ry="14" fill="#5B4530" opacity="0.4" />

      {/* Wavy ground detail at the base */}
      <path
        d="M 0 278
           Q 40 270 80 276
           Q 120 282 160 276
           Q 200 270 240 276
           Q 280 282 320 276
           Q 360 270 400 278
           L 400 320
           L 0 320 Z"
        fill="#3E2A18"
      />
    </svg>
  );
}

/**
 * Quiet empty state used when the server has no currently-featured live
 * article. Keeps the landing page's rhythm without leaving an obvious
 * hole — matches the 320px-tall footprint of the real card.
 */
function FeaturedEmptyState() {
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px dashed #E3D5B8',
        borderRadius: '22px',
        padding: '32px',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '8px',
      }}
    >
      <div
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#1F6A6A',
        }}
      >
        Featured article
      </div>
      <h2
        style={{
          fontFamily: '"DM Serif Display", serif',
          fontWeight: 400,
          fontSize: '26px',
          lineHeight: 1.1,
          color: '#1F1B16',
          margin: 0,
        }}
      >
        Nothing featured just yet
      </h2>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '13px',
          color: '#8A7F72',
          margin: 0,
          maxWidth: '380px',
        }}
      >
        Check back soon — an editor will promote the next article to the top of Ollie Learns.
      </p>
    </div>
  );
}
